'use client'
import * as React from 'react'
import { ArrowRight, Check, Download } from 'lucide-react'
import { SectionEyebrow } from '@/components/marketing/section-eyebrow'
import { IdukkiTile } from '@/components/marketing/idukki-tile'
import { twoTone } from '@/lib/two-tone'
import type {
  PlatformInstallMechanic,
  InstallUiStep,
  InstallEnterpriseStep,
  InstallCodeStep,
} from '@/content/platforms'

// Install-mechanic CENTREPIECE. Names the platform's real integration primitive
// and renders its actual surface, switchable by the step rail:
//   ui         — a recognisable admin / theme-editor mock (breadcrumb + action card)
//   enterprise — a deploy command rail (terminal/admin path + a verified note)
//   code       — a runnable code surface (script tag / SDK / API), JetBrains Mono
//
// Cobalt is the only action colour; lime stays on the IdukkiTile logo mark only.
// Step selection is local state; the active rail indicator slides with spring
// easing and is reduced-motion safe (transform-only, gated in globals).

const MONO: React.CSSProperties = { fontFamily: 'var(--font-jetbrains-mono), ui-monospace, monospace' }

function WindowDots({ dark = false }: { dark?: boolean }) {
  const c = dark ? 'bg-white/15' : 'bg-border'
  return (
    <span className="flex gap-1.5" aria-hidden>
      <span className={`h-2.5 w-2.5 rounded-full ${c}`} />
      <span className={`h-2.5 w-2.5 rounded-full ${c}`} />
      <span className={`h-2.5 w-2.5 rounded-full ${c}`} />
    </span>
  )
}

function UiSurface({ step }: { step: InstallUiStep }) {
  return (
    <div className="motion-safe:animate-[platform-swap_240ms_var(--ease-out)_both]">
      <div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-3">
        <WindowDots />
        <span className="ml-1 min-w-0 truncate text-[11px] text-muted-foreground" style={MONO}>
          {step.breadcrumb}
        </span>
      </div>
      <div className="flex min-h-[300px]">
        {/* admin sidebar skeleton */}
        <div className="hidden w-32 shrink-0 flex-col gap-3 border-r p-4 sm:flex" aria-hidden>
          <span className="h-2 w-4/5 rounded bg-muted" />
          <span className="h-2 w-3/5 rounded bg-muted" />
          <span className="h-2 w-[72%] rounded bg-muted" />
          <span className="h-2 w-1/2 rounded bg-primary/20" />
          <span className="h-2 w-2/3 rounded bg-muted" />
        </div>
        <div className="grid flex-1 place-items-center p-6">
          <div className="w-full max-w-sm rounded-2xl border border-primary/25 bg-[color-mix(in_srgb,var(--accent)_8%,var(--card))] p-5">
            <div className="mb-3.5 flex items-center gap-3">
              <IdukkiTile size="lg" className="shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold tracking-tight text-foreground">{step.actionTitle}</div>
                <div className="mt-0.5 text-[13px] text-muted-foreground">{step.actionDesc}</div>
              </div>
            </div>
            <button
              type="button"
              tabIndex={-1}
              aria-hidden
              className="w-full rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              {step.btn}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EnterpriseSurface({ step }: { step: InstallEnterpriseStep }) {
  return (
    <div className="motion-safe:animate-[platform-swap_240ms_var(--ease-out)_both]">
      <div className="flex items-center justify-between border-b bg-muted/40 px-5 py-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">{step.env}</span>
        <span className="text-[11px] text-muted-foreground" style={MONO}>enterprise deploy</span>
      </div>
      <div className="p-6">
        <div className="rounded-xl bg-brand-navy p-4">
          <code className="block whitespace-pre-wrap break-words text-[13px] leading-relaxed text-white/90" style={MONO}>
            <span className="text-primary-foreground/60">$ </span>
            {step.command}
          </code>
        </div>
        <div className="mt-5 flex items-start gap-3">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Check className="h-3.5 w-3.5" aria-hidden />
          </span>
          <div>
            <p className="text-sm leading-relaxed text-foreground/80">{step.note}</p>
            {step.noteEnv && (
              <span className="mt-2 inline-block rounded-md border bg-muted/40 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                {step.noteEnv}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function CodeSurface({ step }: { step: InstallCodeStep }) {
  return (
    <div className="motion-safe:animate-[platform-swap_240ms_var(--ease-out)_both]">
      <div className="flex items-center gap-2 border-b border-white/10 bg-[color-mix(in_srgb,hsl(var(--brand-navy))_88%,white)] px-4 py-3">
        <WindowDots dark />
        <span className="ml-1 rounded-md bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground">
          {step.lang}
        </span>
      </div>
      <div className="min-h-[280px] bg-brand-navy p-5">
        <pre className="m-0 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-white/85" style={MONO}>
          {step.snippet}
        </pre>
      </div>
    </div>
  )
}

export function PlatformInstallMechanic({
  name,
  mechanic,
}: {
  name: string
  mechanic: PlatformInstallMechanic
}) {
  const [active, setActive] = React.useState(0)
  const steps = mechanic.steps
  const step = steps[Math.min(active, steps.length - 1)]

  return (
    <section id="install" className="border-y bg-muted/20">
      <div className="container-wide py-16 md:py-24">
        <div className="max-w-2xl">
          <SectionEyebrow>How it installs here</SectionEyebrow>
          <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight md:text-4xl text-balance">
            {twoTone(`${mechanic.title}, ${name}`)}
          </h2>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base text-pretty">{mechanic.sub}</p>
          <a
            href="#lead"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-[gap] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:gap-3 focus-visible:outline-none focus-visible:[box-shadow:var(--ring)] rounded"
          >
            <span className="grid h-5 w-5 place-items-center rounded-md bg-primary/10">
              <Download className="h-3 w-3" aria-hidden />
            </span>
            Install on {name} with the {mechanic.primitive.toLowerCase()}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </a>
        </div>

        <div className="mt-9 grid gap-6 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] lg:gap-10">
          {/* step rail */}
          <div className="relative">
            <ol className="flex flex-col gap-3" role="tablist" aria-label={`Install steps for ${name}`}>
              {steps.map((s, i) => {
                const on = i === active
                return (
                  <li key={s.title}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={on}
                      onClick={() => setActive(i)}
                      className={`flex w-full items-start gap-3.5 rounded-2xl border p-4 text-left transition-[border-color,box-shadow,transform] duration-[var(--dur)] ease-[var(--ease-out)] focus-visible:outline-none focus-visible:[box-shadow:var(--ring)] ${
                        on
                          ? 'border-primary bg-card shadow-lg shadow-primary/10'
                          : 'border-border bg-card/60 hover:border-primary/30'
                      }`}
                    >
                      <span
                        className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[13px] font-bold transition-colors duration-[var(--dur)] ${
                          on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[15px] font-semibold tracking-tight text-foreground">{s.title}</span>
                        <span className="mt-1 block text-[13px] leading-relaxed text-muted-foreground">{s.blurb}</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ol>
            <p className="mt-4 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Real integration primitive: <span className="font-semibold text-foreground/70">{mechanic.primitive}</span>
            </p>
          </div>

          {/* surface stage */}
          <div className="overflow-hidden rounded-3xl border bg-card shadow-xl shadow-primary/5">
            {mechanic.surface === 'ui' && <UiSurface step={step as InstallUiStep} key={`ui-${active}`} />}
            {mechanic.surface === 'enterprise' && (
              <EnterpriseSurface step={step as InstallEnterpriseStep} key={`ent-${active}`} />
            )}
            {mechanic.surface === 'code' && <CodeSurface step={step as InstallCodeStep} key={`code-${active}`} />}
          </div>
        </div>
      </div>
    </section>
  )
}
